import React, { useEffect, useMemo, useRef, useState } from "react";
import { getApiBaseUrl } from "../utils/apiConfig";
import "./AdminDashboard.css";

const API = getApiBaseUrl();

/* ------------------------ tiny utils & hooks ------------------------ */
function useReveal(delay = 60) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOn(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return on;
}

/** Count up numbers using requestAnimationFrame (no external libs) */
function CountUp({ to = 0, duration = 900, money = false, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const startTs = useRef(null);
  const toNum = Number(to || 0);

  useEffect(() => {
    startTs.current = null;
    let raf = 0;
    const step = (ts) => {
      if (!startTs.current) startTs.current = ts;
      const p = Math.min(1, (ts - startTs.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(toNum * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [toNum, duration]);

  const formatted = money
    ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(val).toLocaleString();

  return <>{prefix}{formatted}{suffix}</>;
}

/* ----------------------------- main page ---------------------------- */
export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState({
    cards: {
      totalRegistrations: 0,
      pendingApprovals: 0,
      totalBookings: 0,
      activeVisaApplications: 0,
      pendingWithdrawals: 0,
    },
    members: { hotelOwners: 0, guides: 0, travelers: 0 },
    monthlyUsers: { labels: [], values: [] },   // ['YYYY-MM'], [counts]
    reservations: { labels: [], values: [] },   // ['Mar 23',...], [counts]
    revenue: { total: 0, percent: 0 },
    avgBooking: { value: 0, percent: 0 },
    recentActivity: [],
    newRegistrations: [],
  });

  // ✅ move this hook BEFORE any conditional returns
  const animateOn = useReveal(90);

  const topCards = useMemo(() => ([
    { title: "Total Registrations", value: data.cards.totalRegistrations, sub: "All users", icon: "📄" },
    { title: "Pending Approvals", value: data.cards.pendingApprovals, sub: "Pending Approvals", icon: "🕐", prefix: "+" },
    { title: "New Bookings", value: data.cards.totalBookings, sub: "Bookings and Tour bookings", icon: "🧾", prefix: "+" },
    { title: "Active visa applications", value: data.cards.activeVisaApplications, sub: "Active Visa Applications", icon: "🛂", prefix: "+" },
    { title: "Pending Withdrawal", value: data.cards.pendingWithdrawals, sub: "Pending Payouts", icon: "💸", prefix: "LKR ", money: true },
  ]), [data.cards]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setErr("");
        const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API}/admin/dashboard`, { headers });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || "Failed to fetch dashboard.");
        if (mounted) setData(json);
      } catch (e) {
        if (mounted) setErr(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const membersBars = useMemo(() => {
    const m = data.members;
    return [
      { label: "Hotel Owners", value: m.hotelOwners },
      { label: "Guides", value: m.guides },
      { label: "Travelers", value: m.travelers },
    ];
  }, [data.members]);

  if (loading) {
    return (
      <>
        <section className="cards">
          {[...Array(5)].map((_, i) => (
            <div className="card stat" key={i} style={{ opacity: 0.6 }}>
              <div className="lock" aria-hidden>…</div>
              <div className="stat-meta">
                <div className="stat-title">Loading…</div>
                <div className="stat-value">—</div>
                <div className="stat-sub">—</div>
              </div>
            </div>
          ))}
        </section>
      </>
    );
  }

  if (err) {
    return (
      <div className="card" style={{ padding: 24, color: "crimson" }}>
        {err}
      </div>
    );
  }

  const { monthlyUsers, reservations, revenue, avgBooking } = data;

  // helpers
  const maxMonthly = Math.max(...monthlyUsers.values, 0);
  const maxRes = Math.max(1, ...(reservations.values || []));

  return (
    <>
      {/* Stat cards */}
      <section className="cards">
        {topCards.map((s, i) => (
          <div className="card stat" key={i}>
            <div className="lock" aria-hidden>{s.icon}</div>
            <div className="stat-meta">
              <div className="stat-title">{s.title}</div>
              <div className="stat-value"><CountUp to={s.value} prefix={s.prefix || ""} money={s.money || false} /></div>
              <div className="stat-sub">{s.sub}</div>
            </div>
          </div>
        ))}
      </section>

      {/* Middle strip */}
      <section className="grid-2">
        {/* Members availability */}
        <div className="card member-card fade-in">
          <div className="card-head">
            <h3>Members Availability</h3>
            <div className="dots">•••</div>
          </div>

          <div className="mbars">
            {membersBars.map((m, i) => (
              <div
                key={m.label}
                className={`mbar b${i + 1}`}
                style={{
                  flexGrow: m.value,
                  flexBasis: 0,
                  transform: `scaleX(${animateOn ? 1 : 0})`,
                  transformOrigin: "left center",
                  transition: `transform 600ms ${150 + i * 80}ms cubic-bezier(.2,.7,.2,1)`
                }}
                aria-label={`${m.label} ${m.value}`}
                title={`${m.label}: ${m.value}`}
              />
            ))}
          </div>

          <div className="mmetrics">
            {membersBars.map((m, i) => (
              <div className="mmetric" key={m.label} style={{ opacity: animateOn ? 1 : 0, transition: `opacity 420ms ${200 + i * 60}ms ease` }}>
                <span className="tick" />
                <div>
                  <div className="mlabel">{m.label}</div>
                  <div className="mvalue"><CountUp to={m.value} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly user growth */}
        <div className="card fade-in">
          <div className="card-head">
            <h3>Monthly User Growth</h3>
            <div className="seg"><span className="seg-icon" aria-hidden>📅</span> Monthly</div>
          </div>

          <div className="mug-wrap">
            {/* Y axis ticks */}
            <div className="mug-yaxis">
              <div>100%</div>
              <div>75%</div>
              <div>50%</div>
              <div>25%</div>
              <div>0%</div>
            </div>

            {/* Chart */}
            <div className="mug-chart">
              <div className="mug-grid">
                {[100, 75, 50, 25, 0].map((t) => <div className="mug-gridline" key={t} />)}
              </div>

              {monthlyUsers.values.map((v, i) => {
                const percent = maxMonthly > 0 ? Math.round((v / maxMonthly) * 100) : 0;
                return (
                  <div
                    className="mug-col"
                    key={i}
                    title={`${shortMonth(monthlyUsers.labels[i])}: ${v} users`}
                  >
                    <div
                      className="mug-bar"
                      style={{
                        height: animateOn ? `${percent}%` : "0%",
                        transitionDelay: `${120 + i * 40}ms`
                      }}
                    />
                    <div className="mug-label">{shortMonth(monthlyUsers.labels[i])}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Charts row */}
      <section className="grid-3">
        {/* Reservations (last 12 days) */}
        <div className="card fade-in">
          <div className="card-head">
            <h3>Reservations</h3>
          </div>

          <div className="resv-top">
            <div className="resv-total">
              +<CountUp to={(reservations.values || []).reduce((a, b) => a + b, 0)} />
            </div>
            <div className="resv-sub">Total reservations in last 12 days</div>
          </div>

          <div className="mug-wrap">
            {/* Left Y axis in % like MUG */}
            <div className="mug-yaxis">
              {[100, 75, 50, 25, 0].map((t) => (
                <div key={t}>{t}%</div>
              ))}
            </div>

            {/* Same bar grid + faint horizontal lines as MUG */}
            <div className="mug-chart">
              <div className="mug-grid">
                {[100, 75, 50, 25, 0].map((t) => (
                  <div className="mug-gridline" key={t} />
                ))}
              </div>

              {(reservations.values || []).map((v, i) => {
                const h = Math.round((v / maxRes) * 100);
                return (
                  <div className="mug-col" key={i} title={`${reservations.labels[i]}: ${v}`}>
                    <div
                      className="mug-bar"
                      style={{
                        height: animateOn ? `${h}%` : "0%",
                        transitionDelay: `${120 + i * 40}ms`
                      }}
                    />
                    <div className="mug-label">{reservations.labels[i]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Revenue (semi-gauge) */}
        <GaugeCard
          title="Total Revenue"
          value={`LKR ${Number(revenue.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          percent={revenue.percent}
          subtitle={`Ratio of paid revenue: ${revenue.percent}%`}
          animate
        />

        {/* Average booking value (semi-gauge) */}
        <GaugeCard
          title="Average Booking Value"
          value={`LKR ${Number(avgBooking.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          percent={avgBooking.percent}
          subtitle="Value relative to target average"
          animate
        />
      </section>

      {/* Tables */}
      <section className="grid-2">
        {/* Recent Activity */}
        <div className="card table-card fade-in">
          <div className="card-head">
            <h3>Recent Activity</h3>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>User Name</th>
                <th>Role</th>
                <th>Activity</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {data.recentActivity.map((r, i) => (
                <tr key={i} style={{ animation: `rowIn 380ms ${i * 40}ms both ease-out` }}>
                  <td>{r.user}</td>
                  <td>{r.role}</td>
                  <td>{r.activity}</td>
                  <td className="muted">{fmtDate(r.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* New Registrations (latest 4) */}
        <div className="card table-card fade-in">
          <div className="card-head">
            <h3>New Registrations</h3>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Register number</th>
                <th>Name</th>
                <th>Role</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.newRegistrations.map((r, i) => (
                <tr key={i} style={{ animation: `rowIn 380ms ${i * 40}ms both ease-out` }}>
                  <td>{r.id}</td>
                  <td>{r.name}</td>
                  <td>{r.role}</td>
                  <td className="muted">{fmtDate(r.date)}</td>
                  <td><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/* --- helpers & local components --- */
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}
function shortMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: "short" });
}

// function StatCard({ title, value, delta }) {
//   return (
//     <div className="stat-card fade-in">
//       <div className="stat-top"><span className="muted">{title}</span></div>
//       <div className="stat-val">{value}</div>
//       <div className="stat-sub">{delta}</div>
//     </div>
//   );
// }

/** Animated semi-gauge that draws from 0 to percent */
function GaugeCard({ title, value, percent = 0, subtitle, animate = false }) {
  const [p, setP] = useState(animate ? 0 : Math.max(0, Math.min(100, Number(percent) || 0)));
  const target = Math.max(0, Math.min(100, Number(percent) || 0));

  useEffect(() => {
    if (!animate) return;
    let raf = 0;
    const start = performance.now();
    const dur = 900;
    const tick = (ts) => {
      const t = Math.min(1, (ts - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setP(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animate, target]);

  // SVG semi-gauge setup
  const size = 160, cx = size / 2, cy = size / 2, r = 60, sw = 12;
  const startAngle = -180, endAngleBg = 0;
  const endAngleFg = startAngle + ((endAngleBg - startAngle) * p) / 100;

  const describeArc = (cx, cy, r, start, end) => {
    const rad = (deg) => (Math.PI / 180) * deg;
    const sx = cx + r * Math.cos(rad(start));
    const sy = cy + r * Math.sin(rad(start));
    const ex = cx + r * Math.cos(rad(end));
    const ey = cy + r * Math.sin(rad(end));
    const largeArcFlag = end - start <= 180 ? 0 : 1;
    return `M ${sx} ${sy} A ${r} ${r} 0 ${largeArcFlag} 1 ${ex} ${ey}`;
  };

  const bgPath = describeArc(cx, cy, r, startAngle, endAngleBg);
  const fgPath = describeArc(cx, cy, r, startAngle, endAngleFg);

  return (
    <div className="card gauge-card fade-in">
      <div className="gauge-top">
        <div className="gauge-title">{title}</div>
        <div className="gauge-value">{value}</div>
        {subtitle ? <div className="gauge-sub">{subtitle}</div> : null}
      </div>

      <div className="gauge-wrap" role="img" aria-label={`${title} ${p}%`}>
        <svg width="100%" height="120" viewBox={`0 0 ${size} ${size / 1.6}`}>
          <path d={bgPath} stroke="#E7EDF6" strokeWidth={sw} fill="none" strokeLinecap="round" />
          <path d={fgPath} stroke="var(--primary)" strokeWidth={sw} fill="none" strokeLinecap="round" />
        </svg>
        <div className="gauge-center">{p}%</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`pill ${String(status || '').toLowerCase()}`}>{status}</span>;
}
